import { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Film, Image as ImageIcon, Send, CheckCircle2, Camera, MessageSquare, Users, Stethoscope, Clock, AlertTriangle, Check, ListChecks, FileText, CalendarClock } from 'lucide-react';
import { api } from '@/lib/api';
import { useAuth } from '@/hooks/use-auth';
import MyChannelsCard from '@/components/connect/MyChannelsCard';
import { cn } from '@/lib/cn';
import { myRequests } from '@/lib/requests';
import StepLine from '@/components/portal/StepLine';
import type { PortalStep as PDStep } from '@/lib/queries';

interface PortalCreative {
  id: string; code: string; title: string; creative_type: string; state: string; posting_date?: string | null; talent_id?: string | null; talent_name?: string | null;
  latest_asset_thumbnail_url?: string | null; latest_asset_content_type?: string | null; client_approved_at?: string | null; client_awaiting_you?: boolean | null;
}
interface Dashboard {
  client_name: string;
  me_talent_id?: string | null;
  reach?: string[] | null;
  pending_on_you: { creative_id: string; code: string; title: string; talent_name?: string | null; since?: string | null }[];
  doctor_scripts: { creative_id: string; code: string; title: string; doctor?: string | null; talent_id?: string | null; mine: boolean; sent_at?: string | null; status: string }[];
  doctors: { id: string; name: string; telegram_linked: boolean; portal_linked: boolean; me: boolean }[];
  open_requests: { id: string; title: string; status: string; created_at: string }[];
  in_production: { creative_id: string; code: string; title: string; creative_type: string; talent_id?: string | null; talent_name?: string | null; steps: PDStep[]; next_label: string; waiting_on_you: boolean; expected_by?: string | null; shoot_at?: string | null; client_visible: boolean }[];
  upcoming_shoots: { id: string; title: string; shoot_at: string; location?: string | null; doctors: string[]; crew: string[]; creative_count: number }[];
  team: { name: string; designations: string[] }[];
  chat_unread: number;
  counts: Record<string, number>;
}

const fmt = (iso?: string | null, withTime = false) => iso ? new Date(iso).toLocaleString('en-AE', withTime ? { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' } : { day: 'numeric', month: 'short' }) : '';

/** The clinic's home: what needs them, what KAF is doing for them, who is on it. */
export default function PortalHomePage() {
  const { user: me } = useAuth();
  if (me?.portal_scope === 'uploads') return <Navigate to="/portal/material" replace />;
  const { user } = useAuth();
  const dash = useQuery({ queryKey: ['portal-dashboard'], queryFn: async () => (await api.get<Dashboard>('/portal/dashboard')).data, refetchInterval: 30000 });
  const q = useQuery({ queryKey: ['portal-creatives'], queryFn: async () => (await api.get<PortalCreative[]>('/creatives')).data });
  const d = dash.data;
  // A login that covers several doctors can narrow the page to one of them.
  const [doc, setDoc] = useState('');
  const docName = doc ? (d?.doctors.find((t) => t.id === doc)?.name ?? '') : '';
  const inProd = (d?.in_production ?? []).filter((c) => !doc || c.talent_id === doc);
  const pendingRows = (d?.pending_on_you ?? []).filter((p) => !doc || p.talent_name === docName);
  const scriptRows = (d?.doctor_scripts ?? []).filter((x) => !doc || x.talent_id === doc);
  const work = (q.data ?? []).filter((c) => !doc || c.talent_id === doc);
  const forYou = d?.counts.scripts_for_you ?? 0;
  // What is actually on the clinic's side: approvals waiting for them, plus a script
  // still sitting with their doctor. A request they sent us, and a script the doctor
  // has already answered, are on our side.
  // a request where KAF replied and waits on the clinic is on their side too (requests v2)
  const myReqs = useQuery({ queryKey: ['portal-requests'], queryFn: myRequests });
  const yourTurn = new Set((myReqs.data ?? []).filter((r) => r.status === 'new' && r.waiting_on === 'clinic').map((r) => r.id));
  const needs = (d?.counts.pending_on_you ?? 0)
    + (d?.doctor_scripts.filter((x) => x.status === 'sent').length ?? 0) + yourTurn.size;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold text-muted-strong">{d ? d.client_name : 'Your clinic'}</h1>
          <p className="text-xs text-muted mt-1">{needs ? `${needs} thing${needs === 1 ? '' : 's'} waiting on you.` : 'Nothing is waiting on you right now.'}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Link to="/portal/track" className="h-9 px-3 rounded-lg border border-slate-800 text-xs text-muted-strong inline-flex items-center gap-2 hover:border-accent-400/40 transition" data-testid="go-track">
            <ListChecks size={13} /> Tracker
          </Link>
          <Link to="/portal/scripts" className="h-9 px-3 rounded-lg border border-slate-800 text-xs text-muted-strong inline-flex items-center gap-2 hover:border-accent-400/40 transition" data-testid="go-scripts">
            <FileText size={13} /> Scripts {forYou > 0 && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-500 text-white">{forYou}</span>}
          </Link>
          <Link to="/portal/chat" className="h-9 px-3 rounded-lg border border-slate-800 text-xs text-muted-strong inline-flex items-center gap-2 hover:border-accent-400/40 transition">
            <MessageSquare size={13} /> Chat {d && d.chat_unread > 0 && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-accent-500 text-white">{d.chat_unread}</span>}
          </Link>
          <Link to="/portal/meetings?new=1" className="h-9 px-3 rounded-lg border border-slate-800 text-xs text-muted-strong inline-flex items-center gap-2 hover:border-accent-400/40 transition" data-testid="go-meeting">
            <CalendarClock size={13} /> Request a meeting
          </Link>
          <Link to="/portal/requests?new=1" className="h-9 px-4 rounded-lg bg-accent-600 hover:bg-accent-500 text-white text-xs font-medium inline-flex items-center gap-2 transition">
            <Send size={13} /> Request a creative
          </Link>
        </div>
      </div>

      {d && d.doctors.length > 1 && (
        <div className="flex flex-wrap gap-2" data-testid="doctor-chips">
          <button onClick={() => setDoc('')} className={cn('text-xs px-3 py-1.5 min-h-[36px] rounded-full border transition', !doc ? 'border-accent-400 bg-accent-600/20 text-accent-400' : 'border-slate-800 text-muted hover:text-muted-strong')}>All doctors</button>
          {d.doctors.map((t) => (
            <button key={t.id} onClick={() => setDoc(t.id)} className={cn('text-xs px-3 py-1.5 min-h-[36px] rounded-full border transition', doc === t.id ? 'border-accent-400 bg-accent-600/20 text-accent-400' : 'border-slate-800 text-muted hover:text-muted-strong')}>{t.me ? `You (${t.name})` : t.name}</button>
          ))}
        </div>
      )}
      {forYou > 0 && (
        <Link to="/portal/scripts" className="block rounded-xl border border-amber-500/50 bg-amber-500/10 px-4 py-3 hover:border-amber-400 transition" data-testid="scripts-for-you">
          <div className="flex items-center gap-2 text-sm"><FileText size={14} className="text-amber-300" /><span className="text-muted-strong font-medium">{forYou} script{forYou === 1 ? '' : 's'} waiting for your approval</span><span className="ml-auto text-xs text-amber-300">Read and answer</span></div>
        </Link>
      )}
      {d && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Tile label="Waiting on you" value={d.counts.pending_on_you} tone={d.counts.pending_on_you ? 'accent' : undefined} icon={<Clock size={12} />} />
          <Tile label="In production" value={d.counts.in_production} icon={<Film size={12} />} />
          <Tile label="Approved" value={d.counts.approved} icon={<Check size={12} />} />
          <Tile label="Posted" value={d.counts.posted} icon={<CheckCircle2 size={12} />} />
        </div>
      )}

      {/* Pending on your side */}
      {d && (pendingRows.length > 0 || scriptRows.length > 0 || d.open_requests.length > 0) && (
        <section className="space-y-2">
          <h2 className="text-xs uppercase tracking-wide text-muted">On your side</h2>
          {pendingRows.map((p) => (
            <Link key={p.creative_id} to={`/portal/creatives/${p.creative_id}`} className="block rounded-xl border border-accent-400/40 bg-accent-600/10 px-4 py-3 hover:border-accent-400 transition" data-testid="pd-pending">
              <div className="flex items-center gap-2 flex-wrap text-sm">
                <span className="font-mono text-[10px] text-muted">{p.code}</span>
                <span className="text-muted-strong font-medium">{p.title}</span>
                <span className="ml-auto text-xs text-accent-300">Approve or ask for a change</span>
              </div>
              {p.since && <div className="text-[11px] text-muted mt-0.5">waiting since {fmt(p.since, true)}</div>}
            </Link>
          ))}
          {scriptRows.map((s) => (
            <Link to="/portal/scripts" key={s.creative_id} className={cn('block rounded-xl border px-4 py-3 transition', s.status === 'changes_requested' ? 'border-slate-800 bg-bg-card' : s.mine ? 'border-amber-400 bg-amber-500/10 hover:border-amber-300' : 'border-amber-500/40 bg-amber-500/5 hover:border-amber-400')} data-testid="pd-doctor">
              <div className="flex items-center gap-2 flex-wrap text-sm">
                <Stethoscope size={13} className="text-amber-300" />
                <span className="font-mono text-[10px] text-muted">{s.code}</span>
                <span className="text-muted-strong">{s.title}</span>
                <span className={cn('ml-auto text-xs', s.mine && s.status === 'sent' ? 'text-amber-300 font-medium' : 'text-muted')}>{s.status === 'sent' ? (s.mine ? 'waiting for you: read and approve' : `script with ${s.doctor ?? 'your doctor'}${s.sent_at ? ` since ${fmt(s.sent_at)}` : ''}`) : `${s.doctor ?? 'your doctor'} asked for changes; KAF is revising`}</span>
              </div>
            </Link>
          ))}
          {d.open_requests.map((r) => (
            <Link key={r.id} to={`/portal/requests/${r.id}`} className={cn('block rounded-xl border bg-bg-card px-4 py-3 hover:border-accent-400/40 transition', yourTurn.has(r.id) ? 'border-amber-400/60' : 'border-slate-800')}>
              <div className="flex items-center gap-2 flex-wrap text-sm"><Send size={13} className="text-muted" /><span className="text-muted-strong" dir="auto">{r.title}</span>
                <span className={cn('ml-auto text-xs', yourTurn.has(r.id) ? 'text-amber-300 font-medium' : 'text-muted')}>
                  {yourTurn.has(r.id) ? <>KAF replied, your turn · <span lang="ar">بانتظار ردك</span></> : <>your request · waiting for KAF · {fmt(r.created_at)}</>}</span></div>
            </Link>
          ))}
        </section>
      )}

      {/* In production */}
      {d && inProd.length > 0 && (
        <section className="space-y-2">
          <h2 className="text-xs uppercase tracking-wide text-muted">In production for you</h2>
          {inProd.map((c) => (
            <div key={c.creative_id} className="rounded-xl border border-slate-800 bg-bg-card px-4 py-3" data-testid="pd-production">
              <div className="flex items-center gap-2 flex-wrap text-sm">
                <span className="font-mono text-[10px] text-muted">{c.code}</span>
                <span className="text-muted-strong font-medium truncate">{c.title}</span>
                <span className="text-[10px] uppercase text-muted">{c.creative_type}</span>
                {c.talent_name && !doc && <span className="text-[10px] text-muted">{c.talent_name}</span>}
                <span className={cn('ml-auto text-xs', c.waiting_on_you ? 'text-accent-300 font-medium' : 'text-muted')}>{c.next_label}{c.expected_by && !c.waiting_on_you ? ` · expected by ${fmt(c.expected_by)}` : ''}</span>
              </div>
              <StepLine steps={c.steps} />
              {c.client_visible && <Link to={`/portal/creatives/${c.creative_id}`} className="text-[11px] text-accent-400 hover:underline mt-1 inline-block">Open</Link>}
            </div>
          ))}
        </section>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <section className="rounded-xl border border-slate-800 bg-bg-card p-4">
          <h2 className="text-xs uppercase tracking-wide text-muted inline-flex items-center gap-1 mb-2"><Camera size={11} /> Upcoming shoots</h2>
          {d && d.upcoming_shoots.length === 0 && <p className="text-xs text-muted">None planned yet.</p>}
          {d?.upcoming_shoots.map((s) => (
            <div key={s.id} className="py-1.5 border-t border-slate-800/60 first:border-t-0 text-xs">
              <div className="text-muted-strong">{fmt(s.shoot_at, true)}</div>
              <div className="text-muted">{s.location || 'place to be confirmed'}{s.doctors.length ? ` · ${s.doctors.join(', ')}` : ''}{s.crew.length ? ` · with ${s.crew.join(', ')}` : ''} · {s.creative_count} creative{s.creative_count === 1 ? '' : 's'}</div>
            </div>
          ))}
        </section>
        <section className="rounded-xl border border-slate-800 bg-bg-card p-4">
          <h2 className="text-xs uppercase tracking-wide text-muted inline-flex items-center gap-1 mb-2"><Stethoscope size={11} /> Your doctors</h2>
          {d && d.doctors.length === 0 && <p className="text-xs text-muted">No doctors listed yet.</p>}
          {d?.doctors.map((t) => (
            <div key={t.id} className="py-1 flex items-center gap-2 text-xs">
              <span className={cn('w-2 h-2 rounded-full', t.telegram_linked || t.portal_linked ? 'bg-emerald-500' : 'bg-slate-700')} />
              <span className="text-muted-strong">{t.name}{t.me ? ' (you)' : ''}</span>
              <span className="text-muted ml-auto">{[t.telegram_linked ? 'Telegram' : '', t.portal_linked ? 'portal' : ''].filter(Boolean).join(' + ') || 'not connected'}</span>
            </div>
          ))}
          {d && d.doctors.some((t) => !t.telegram_linked && !t.portal_linked) && <p className="text-[11px] text-muted mt-2 inline-flex items-center gap-1"><AlertTriangle size={10} /> Ask KAF to connect the doctor on Telegram or invite them to the portal, so scripts reach them directly.</p>}
        </section>
        <section className="rounded-xl border border-slate-800 bg-bg-card p-4">
          <h2 className="text-xs uppercase tracking-wide text-muted inline-flex items-center gap-1 mb-2"><Users size={11} /> Your KAF team</h2>
          {d && d.team.length === 0 && <p className="text-xs text-muted">Being assigned.</p>}
          {d?.team.map((p) => (
            <div key={p.name} className="py-1 text-xs"><span className="text-muted-strong">{p.name}</span> <span className="text-muted">{p.designations.map((x) => x.replace(/_/g, ' ')).join(', ')}</span></div>
          ))}
          <Link to="/portal/chat" className="text-[11px] text-accent-400 hover:underline mt-2 inline-block">Write to them</Link>
        </section>
      </div>

      {/* Approved work */}
      <section className="space-y-3">
        <div>
          <h2 className="text-sm font-semibold text-muted-strong">Your work</h2>
          <p className="text-xs text-muted">Ready for your review and approval, newest first.</p>
        </div>
        {q.isLoading && <div className="text-sm text-muted py-6 text-center">Loading your work...</div>}
        {q.data && work.length === 0 && (
          <div className="border border-dashed border-bg-card rounded-2xl py-12 text-center space-y-2">
            <p className="text-sm text-muted-strong">Nothing here yet.</p>
            <p className="text-xs text-muted">Approved creatives for {user?.display_name} will appear here the moment the team signs them off.</p>
          </div>
        )}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {work.map((c) => (
            <Link key={c.id} to={`/portal/creatives/${c.id}`} className="group bg-bg-card border border-bg-card hover:border-accent-400/40 rounded-xl overflow-hidden transition">
              <div className="aspect-[4/5] bg-bg-soft relative overflow-hidden">
                {c.client_approved_at && (
                  <span data-testid="tile-approved" className="absolute top-2 right-2 z-10 inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-500 text-white shadow"><CheckCircle2 size={11} /> Approved</span>
                )}
                {c.latest_asset_thumbnail_url ? (
                  <img src={c.latest_asset_thumbnail_url} alt={c.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-300" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-muted/50">{c.creative_type === 'video' || c.creative_type === 'reel' ? <Film size={28} /> : <ImageIcon size={28} />}</div>
                )}
                <span className="absolute top-2 left-2 text-[10px] px-2 py-0.5 rounded-full bg-black/50 text-white backdrop-blur">{c.creative_type}</span>
              </div>
              <div className="p-3 space-y-1">
                <div className="text-[10px] font-mono text-muted">{c.code}</div>
                <div className="text-xs font-medium text-muted-strong leading-snug line-clamp-2">{c.title}</div>
                <div className="flex items-center justify-between pt-0.5">
                  <span className={'text-[10px] px-2 py-0.5 rounded-full ' + (c.state === 'Posted' || c.client_approved_at ? 'bg-emerald-500/15 text-emerald-400' : 'bg-sky-500/15 text-sky-300')}>
                    {c.state === 'Posted' ? 'Posted' : c.client_approved_at ? 'Approved' : c.client_awaiting_you === false ? 'In review' : 'Awaiting your approval'}
                  </span>
                  {c.posting_date && <span className="text-[10px] text-muted">{c.posting_date}</span>}
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>
      {/* Clinic contacts connect their own WhatsApp and confirm their email here too. */}
      <MyChannelsCard name={user?.display_name ?? ''} />
    </div>
  );
}

function Tile({ label, value, tone, icon }: { label: string; value: number; tone?: 'accent'; icon?: React.ReactNode }) {
  return (
    <div className={cn('rounded-xl border px-4 py-3', tone === 'accent' ? 'border-accent-400/40 bg-accent-600/10' : 'border-slate-800 bg-bg-card')}>
      <div className="text-[10px] uppercase tracking-wide text-muted inline-flex items-center gap-1">{icon}{label}</div>
      <div className={cn('text-2xl font-semibold tabular-nums', tone === 'accent' ? 'text-accent-300' : 'text-muted-strong')}>{value ?? 0}</div>
    </div>
  );
}
